import axios from "axios";
import getBaseUrl from "./baseUrl";
import { API_ROUTES, DATA_FILES, } from "../types";
export class ApiClient {
    constructor(baseUrl) {
        this.baseUrl = getBaseUrl();
        this.defaultHeaders = {
            "Content-Type": "application/json",
        };
        this.client = axios.create({
            baseURL: this.baseUrl,
            timeout: 120000,
            headers: this.defaultHeaders,
        });
        this.setupInterceptors();
    }
    // Authentication methods
    async login(credentials) {
        return this.request({
            method: "POST",
            url: API_ROUTES.AUTHORIZE,
            data: credentials,
        });
    }
    async signupFreeTrial(payload) {
        return this.request({
            method: 'POST',
            url: API_ROUTES.FREE_TRIAL_SIGNUP,
            data: payload,
        });
    }
    async getFreeEmailDomains() {
        return this.request({
            method: 'GET',
            url: API_ROUTES.FREE_TRIAL_DOMAINS,
        });
    }
    async checkDomainEligibility(email) {
        return this.request({
            method: 'GET',
            url: API_ROUTES.FREE_TRIAL_CHECK_DOMAIN,
            params: { email },
        });
    }
    async verifyEmail(token, password) {
        return this.request({
            method: 'POST',
            url: API_ROUTES.VERIFY_EMAIL,
            data: password ? { token, password } : { token },
        });
    }
    async checkVerificationToken(token) {
        return this.request({
            method: 'POST',
            url: `${API_ROUTES.VERIFY_EMAIL}/check`,
            data: { token },
        });
    }
    async resendVerification(email) {
        return this.request({
            method: 'POST',
            url: API_ROUTES.RESEND_VERIFICATION,
            data: { email },
        });
    }
    async fetchMemberships() {
        return this.request({
            method: 'GET',
            url: API_ROUTES.MEMBERSHIP_ALL,
        });
    }
    async beginPasskeyLogin(params) {
        return this.request({
            method: "POST",
            url: API_ROUTES.USER_PASSKEY_BEGIN,
            data: params,
        });
    }
    async finishPasskeyLogin(payload) {
        return this.request({
            method: "POST",
            url: API_ROUTES.USER_PASSKEY_FINISH,
            data: payload,
        });
    }
    async beginPasskeyRegistration() {
        return this.request({
            method: "POST",
            url: API_ROUTES.USER_PASSKEY_REGISTER_BEGIN,
        });
    }
    async finishPasskeyRegistration(payload) {
        return this.request({
            method: "POST",
            url: API_ROUTES.USER_PASSKEY_REGISTER_FINISH,
            data: payload,
        });
    }
    async logout() {
        return this.handleUnauthorized();
    }
    async updatePassword(current_password, new_password, user_id) {
        const config = {
            method: "PUT",
            url: API_ROUTES.PASSWORD_UPDATE,
            data: {
                password: {
                    old: current_password,
                    new: new_password
                }
            },
        };
        if (user_id) {
            config.params = { user_id };
        }
        return this.request(config);
    }
    async updateUserProfile(userData) {
        return this.request({
            method: "PUT",
            url: API_ROUTES.UPDATE_PROFILE,
            data: userData,
        });
    }
    async acceptTermsOfUse() {
        return this.request({
            method: "PUT",
            url: API_ROUTES.TERMS_ACCEPT,
            data: {},
        });
    }
    async refreshToken() {
        // Instead of hitting an API, just reset the expiring time for the access token in localStorage
        const token = this.getAccessToken();
        if (token) {
            localStorage.setItem('access_token', token);
            localStorage.setItem('access_token_expiry', String(Date.now() + 3600 * 1000));
            return { accessToken: token };
        }
        else {
            throw new Error('No access token found');
        }
    }
    // Get User Data
    async getUserData() {
        return this.request({
            method: "GET",
            url: API_ROUTES.USER_ROOT,
        });
    }
    async getUserAccessibleReports(user_id) {
        return this.request({
            method: "GET",
            params: { user_id: user_id },
            url: API_ROUTES.REPORT_USER,
        });
    }
    async getReport(reportId) {
        return this.request({
            method: "GET",
            url: `${API_ROUTES.REPORT}${reportId}`,
        });
    }
    async adminGetReports() {
        return this.request({
            method: 'GET',
            url: API_ROUTES.ADMIN_REPORTS,
        });
    }
    async adminCreateReport(data) {
        return this.request({
            method: 'POST',
            url: API_ROUTES.ADMIN_REPORTS,
            data,
        });
    }
    async adminUpdateReport(reportId, data) {
        return this.request({
            method: 'PUT',
            url: `${API_ROUTES.ADMIN_REPORTS}/${reportId}`,
            data,
        });
    }
    async adminDeleteReport(reportId) {
        return this.request({
            method: 'DELETE',
            url: `${API_ROUTES.ADMIN_REPORTS}/${reportId}`,
        });
    }
    async getReportOptions(reportId) {
        return this.request({
            method: "GET",
            url: API_ROUTES.UTILS_REPORT_OPTIONS,
            params: { report_id: reportId }
        });
    }
    async createUserSession(report) {
        return this.request({
            method: "POST",
            url: API_ROUTES.USER_SESSION,
            data: { report_id: report.report_id }
        });
    }
    async closeUserSession() {
        return this.request({
            method: "PUT",
            url: API_ROUTES.USER_SESSION_CLOSE,
            data: {}
        });
    }
    // Save data to FastAPI cache endpoint
    async saveDataToCache(fileKey, data) {
        try {
            const response = await this.client.post(API_ROUTES.CACHE_SAVE, data, {
                params: { filename: DATA_FILES[fileKey] },
            });
            return response.data === true;
        }
        catch (err) {
            console.error(`Error saving data to cache for ${fileKey}:`, err);
            return false;
        }
    }
    // Read data from FastAPI cache endpoint
    async readDataFromCache(fileKey) {
        try {
            const response = await this.client.get(API_ROUTES.CACHE_READ, {
                params: { filename: DATA_FILES[fileKey] },
                headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache',
                },
            });
            const data = response.data;
            return data;
        }
        catch (err) {
            console.error(`Error reading data from cache for ${fileKey}:`, err);
            return null;
        }
    }
    async readDataFromCacheByFilename(fileKey) {
        try {
            const response = await this.client.get(API_ROUTES.CACHE_READ, {
                params: { filename: fileKey },
                headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache',
                },
            });
            const data = response.data;
            return data;
        }
        catch (err) {
            console.error(`Error reading data from cache for ${fileKey}:`, err);
            return null;
        }
    }
    // Data fetching methods
    async fetchCaproData() {
        try {
            const data = await this.request({
                method: "GET",
                url: API_ROUTES.CAPRO,
            });
            await this.saveDataToCache("CAPRO", data);
            return data;
        }
        catch (err) {
            const cached = await this.readDataFromCache("CAPRO");
            if (cached)
                return cached;
            throw err;
        }
    }
    async fetchPetchemData() {
        try {
            const data = await this.request({
                method: "GET",
                url: API_ROUTES.PETCHEM,
            });
            await this.saveDataToCache("PETCHEM", data);
            return data;
        }
        catch (err) {
            const cached = await this.readDataFromCache("PETCHEM");
            if (cached)
                return cached;
            throw err;
        }
    }
    async fetchChlorineData() {
        try {
            const data = await this.request({
                method: "GET",
                url: API_ROUTES.CHLORINE,
            });
            await this.saveDataToCache("CHLORINE", data);
            return data;
        }
        catch (err) {
            const cached = await this.readDataFromCache("CHLORINE");
            if (cached)
                return cached;
            throw err;
        }
    }
    async fetchDriverData() {
        try {
            const data = await this.request({
                method: "GET",
                url: API_ROUTES.DRIVER_COMMENTARY,
            });
            await this.saveDataToCache("DRIVERS", data);
            return data;
        }
        catch (err) {
            const cached = await this.readDataFromCache("DRIVERS");
            if (cached)
                return cached;
            throw err;
        }
    }
    async fetchDriverPriceChangesData() {
        try {
            const data = await this.request({
                method: "GET",
                url: API_ROUTES.DRIVER_PRICE_CHANGES,
            });
            await this.saveDataToCache("DRIVER_PRICE_CHANGES", data);
            return data;
        }
        catch (err) {
            const cached = await this.readDataFromCache("DRIVER_PRICE_CHANGES");
            if (cached)
                return cached;
            throw err;
        }
    }
    async fetchChemOrbisData() {
        try {
            const data = await this.request({
                method: "GET",
                url: API_ROUTES.CHEM_ORBIS,
            });
            await this.saveDataToCache("CHEMORBIS", data);
            return data;
        }
        catch (err) {
            const cached = await this.readDataFromCache("CHEMORBIS");
            if (cached)
                return cached;
            throw err;
        }
    }
    async fetchPetMasterPricingData() {
        try {
            const data = await this.request({
                method: "GET",
                url: API_ROUTES.PET_MASTER_PRICING,
            });
            await this.saveDataToCache("PETMASTERPRICING", data);
            return data;
        }
        catch (err) {
            const cached = await this.readDataFromCache("PETMASTERPRICING");
            if (cached)
                return cached;
            throw err;
        }
    }
    async fetchContractsData() {
        try {
            const data = await this.request({
                method: "GET",
                url: API_ROUTES.CONTRACTS,
            });
            await this.saveDataToCache("CONTRACTS", data);
            return data;
        }
        catch (err) {
            const cached = await this.readDataFromCache("CONTRACTS");
            if (cached)
                return cached;
            throw err;
        }
    }
    async fetchRefineryRatesData() {
        try {
            const data = await this.request({
                method: "GET",
                url: API_ROUTES.REFINERY_RATES,
            });
            await this.saveDataToCache("REFINERY_RATES", data);
            return data;
        }
        catch (err) {
            const cached = await this.readDataFromCache("REFINERY_RATES");
            if (cached)
                return cached;
            throw err;
        }
    }
    async fetchNewsData() {
        try {
            const data = await this.request({
                method: "GET",
                url: API_ROUTES.NEWS,
            });
            await this.saveDataToCache("NEWS_DATA", data);
            return data;
        }
        catch (err) {
            const cached = await this.readDataFromCache("NEWS_DATA");
            if (cached)
                return cached;
            throw err;
        }
    }
    // ACC Monthly Snapshot methods
    async fetchACCPEData() {
        try {
            const data = await this.request({
                method: "GET",
                url: API_ROUTES.ACC_PE,
            });
            await this.saveDataToCache("ACC_PE", data);
            return data;
        }
        catch (err) {
            const cached = await this.readDataFromCache("ACC_PE");
            if (cached)
                return cached;
            throw err;
        }
    }
    async fetchACCPPData() {
        try {
            const data = await this.request({
                method: "GET",
                url: API_ROUTES.ACC_PP,
            });
            await this.saveDataToCache("ACC_PP", data);
            return data;
        }
        catch (err) {
            const cached = await this.readDataFromCache("ACC_PP");
            if (cached)
                return cached;
            throw err;
        }
    }
    async fetchACCPSData() {
        try {
            const data = await this.request({
                method: "GET",
                url: API_ROUTES.ACC_PS,
            });
            await this.saveDataToCache("ACC_PS", data);
            return data;
        }
        catch (err) {
            const cached = await this.readDataFromCache("ACC_PS");
            if (cached)
                return cached;
            throw err;
        }
    }
    async fetchACCPVCData() {
        try {
            const data = await this.request({
                method: "GET",
                url: API_ROUTES.ACC_PVC,
            });
            await this.saveDataToCache("ACC_PVC", data);
            return data;
        }
        catch (err) {
            const cached = await this.readDataFromCache("ACC_PVC");
            if (cached)
                return cached;
            throw err;
        }
    }
    async fetchACCPEDataFinal() {
        try {
            const data = await this.request({
                method: "GET",
                url: API_ROUTES.ACC_PE_FINAL,
            });
            await this.saveDataToCache("ACC_PE_FINAL", data);
            return data;
        }
        catch (err) {
            const cached = await this.readDataFromCache("ACC_PE_FINAL");
            if (cached)
                return cached;
            throw err;
        }
    }
    async fetchACCPPDataFinal() {
        try {
            const data = await this.request({
                method: "GET",
                url: API_ROUTES.ACC_PP_FINAL,
            });
            await this.saveDataToCache("ACC_PP_FINAL", data);
            return data;
        }
        catch (err) {
            const cached = await this.readDataFromCache("ACC_PP_FINAL");
            if (cached)
                return cached;
            throw err;
        }
    }
    async fetchACCPSDataFinal() {
        try {
            const data = await this.request({
                method: "GET",
                url: API_ROUTES.ACC_PS_FINAL,
            });
            await this.saveDataToCache("ACC_PS_FINAL", data);
            return data;
        }
        catch (err) {
            const cached = await this.readDataFromCache("ACC_PS_FINAL");
            if (cached)
                return cached;
            throw err;
        }
    }
    async fetchACCPVCDataFinal() {
        try {
            const data = await this.request({
                method: "GET",
                url: API_ROUTES.ACC_PVC_FINAL,
            });
            await this.saveDataToCache("ACC_PVC_FINAL", data);
            return data;
        }
        catch (err) {
            const cached = await this.readDataFromCache("ACC_PVC_FINAL");
            if (cached)
                return cached;
            throw err;
        }
    }
    // Pricing Forecast methods
    async fetchFairPriceData() {
        try {
            //add params for start_date and end_date in the form of YYYY-MM-DD, where end_date is today and start_date is january 1st of the previous year
            const start_date = new Date(new Date().getFullYear() - 1, 0, 1).toISOString().split('T')[0];
            const end_date = new Date().toISOString().split('T')[0];
            const data = await this.request({
                method: 'GET',
                url: API_ROUTES.FAIR_PRICE,
                params: { start_date, end_date }
            });
            await this.saveDataToCache('FAIR_PRICE', data);
            return data;
        }
        catch (err) {
            const cached = await this.readDataFromCache('FAIR_PRICE');
            if (cached)
                return cached;
            throw err;
        }
    }
    async fetchForecastData() {
        try {
            const data = await this.request({
                method: 'GET',
                url: API_ROUTES.FORECAST,
            });
            await this.saveDataToCache('FORECAST', data);
            return data;
        }
        catch (err) {
            const cached = await this.readDataFromCache('FORECAST');
            if (cached)
                return cached;
            throw err;
        }
    }
    async fetchForecastCommentary() {
        try {
            const data = await this.request({
                method: 'GET',
                url: API_ROUTES.FORECAST_COMMENTARY,
            });
            await this.saveDataToCache('FORECAST_COMMENTARY', data);
            return data;
        }
        catch (err) {
            const cached = await this.readDataFromCache('FORECAST_COMMENTARY');
            if (cached)
                return cached;
            throw err;
        }
    }
    // EIA Forecast methods
    async fetchEiaForecastData() {
        try {
            const data = await this.request({
                method: 'GET',
                url: API_ROUTES.EIA_FORECAST,
            });
            await this.saveDataToCache('EIA_FORECAST', data);
            return data;
        }
        catch (err) {
            const cached = await this.readDataFromCache('EIA_FORECAST');
            if (cached)
                return cached;
            throw err;
        }
    }
    async fetchEmailBulletsData() {
        try {
            const data = await this.request({
                method: 'GET',
                url: API_ROUTES.EMAIL_BULLETS,
            });
            await this.saveDataToCache('EMAIL_BULLETS', data);
            return data;
        }
        catch (err) {
            const cached = await this.readDataFromCache('EMAIL_BULLETS');
            if (cached)
                return cached;
            throw err;
        }
    }
    async fetchDriverChartData() {
        try {
            return await this.request({
                method: "GET",
                url: API_ROUTES.DRIVER_CHARTS_CONFIG
            });
        }
        catch (error) {
            console.error('Error fetching driver chart data:', error);
            throw error;
        }
    }
    // Driver Charts Configuration
    async fetchDriverChartsConfig() {
        try {
            const data = await this.request({
                method: "GET",
                url: API_ROUTES.DRIVER_CHARTS_CONFIG,
            });
            return data;
        }
        catch (err) {
            const cached = await this.readDataFromCacheByFilename("driverCharts.json");
            if (cached)
                return cached;
            throw err;
        }
    }
    // Global Data Refresh Coordination methods
    async getGlobalRefreshStatus() {
        return this.request({
            method: "GET",
            url: API_ROUTES.GLOBAL_REFRESH_STATUS,
            // @ts-ignore - Custom flag to prevent auto-redirect on 401
            skipAuthRedirect: true,
        });
    }
    async requestGlobalRefresh() {
        return this.request({
            method: "POST",
            url: API_ROUTES.GLOBAL_REFRESH_REQUEST,
        });
    }
    async checkAndTriggerRefresh() {
        return this.request({
            method: "POST",
            url: API_ROUTES.GLOBAL_REFRESH_CHECK,
        });
    }
    async resetGlobalRefresh() {
        return this.request({
            method: "POST",
            url: API_ROUTES.GLOBAL_REFRESH_RESET,
        });
    }
    async selectiveRefresh(keys) {
        return this.request({
            method: 'POST',
            url: API_ROUTES.GLOBAL_REFRESH_SELECTIVE,
            data: { keys },
        });
    }
    async refreshMarketIntelPE() {
        return this.selectiveRefresh(['MI_PE_INVENTORY', 'MI_PE_PRODUCTION', 'MI_PE_MARKETS', 'MI_PE_RATES', 'MI_PE_USITC', 'FAIR_PRICE', 'ACC_PE', 'ACC_PE_FINAL', 'PE_COMMENTARY', 'HDPE_COMMENTARY', 'LDPE_COMMENTARY', 'LLDPE_COMMENTARY']);
    }
    async refreshMarketIntelPP() {
        return this.selectiveRefresh(['ACC_PP', 'ACC_PP_FINAL', 'FAIR_PRICE', 'PETCHEM', 'CHEMORBIS', 'MI_PP_RATES', 'MI_PP_INVENTORY', 'MI_PP_MARKETS', 'MI_PP_USITC']);
    }
    async refreshMarketIntelPS() {
        return this.selectiveRefresh(['ACC_PS', 'ACC_PS_FINAL', 'FAIR_PRICE', 'PETCHEM', 'CHEMORBIS', 'MI_PS_RATES', 'MI_PS_INVENTORY', 'MI_PS_MARKETS', 'MI_PS_USITC']);
    }
    async refreshMarketIntelPVC() {
        return this.selectiveRefresh(['ACC_PVC', 'ACC_PVC_FINAL', 'FAIR_PRICE', 'PETCHEM', 'CHEMORBIS', 'MI_PVC_RATES', 'MI_PVC_INVENTORY', 'MI_PVC_MARKETS', 'MI_PVC_USITC']);
    }
    async refreshMarketIntelPET() {
        return this.selectiveRefresh(['REFINERY_RATES', 'PETMASTERPRICING', 'FAIR_PRICE', 'MI_PET_RATES', 'MI_PET_USITC']);
    }
    async refreshMarketIntelAll() {
        return this.selectiveRefresh([
            'FAIR_PRICE', 'PETCHEM', 'CHEMORBIS',
            'ACC_PE', 'ACC_PE_FINAL', 'MI_PE_INVENTORY', 'MI_PE_PRODUCTION', 'MI_PE_MARKETS', 'MI_PE_RATES', 'MI_PE_USITC',
            'ACC_PP', 'ACC_PP_FINAL', 'MI_PP_RATES', 'MI_PP_INVENTORY', 'MI_PP_MARKETS', 'MI_PP_USITC',
            'ACC_PS', 'ACC_PS_FINAL', 'MI_PS_RATES', 'MI_PS_INVENTORY', 'MI_PS_MARKETS', 'MI_PS_USITC',
            'ACC_PVC', 'ACC_PVC_FINAL', 'MI_PVC_RATES', 'MI_PVC_INVENTORY', 'MI_PVC_MARKETS', 'MI_PVC_USITC',
            'REFINERY_RATES', 'PETMASTERPRICING', 'MI_PET_RATES', 'MI_PET_USITC',
        ]);
    }
    async runRefreshAction(actionKey) {
        const map = {
            Capro: { url: API_ROUTES.CAPRO, cacheKey: 'CAPRO' },
            Petchem: { url: API_ROUTES.PETCHEM, cacheKey: 'PETCHEM' },
            Chlorine: { url: API_ROUTES.CHLORINE, cacheKey: 'CHLORINE' },
            Driver: { url: API_ROUTES.DRIVER_COMMENTARY, cacheKey: 'DRIVERS' },
            DriverCharts: { url: API_ROUTES.DRIVER_CHARTS_CONFIG },
            DriverPriceChanges: { url: API_ROUTES.DRIVER_PRICE_CHANGES, cacheKey: 'DRIVER_PRICE_CHANGES' },
            ChemOrbis: { url: API_ROUTES.CHEM_ORBIS, cacheKey: 'CHEMORBIS' },
            PetMasterPricing: { url: API_ROUTES.PET_MASTER_PRICING, cacheKey: 'PETMASTERPRICING' },
            Contracts: { url: API_ROUTES.CONTRACTS, cacheKey: 'CONTRACTS' },
            RefineryRates: { url: API_ROUTES.REFINERY_RATES, cacheKey: 'REFINERY_RATES' },
            ACC_PE: { url: API_ROUTES.ACC_PE, cacheKey: 'ACC_PE' },
            ACC_PP: { url: API_ROUTES.ACC_PP, cacheKey: 'ACC_PP' },
            ACC_PS: { url: API_ROUTES.ACC_PS, cacheKey: 'ACC_PS' },
            ACC_PVC: { url: API_ROUTES.ACC_PVC, cacheKey: 'ACC_PVC' },
            ACC_PE_FINAL: { url: API_ROUTES.ACC_PE_FINAL, cacheKey: 'ACC_PE_FINAL' },
            ACC_PP_FINAL: { url: API_ROUTES.ACC_PP_FINAL, cacheKey: 'ACC_PP_FINAL' },
            ACC_PS_FINAL: { url: API_ROUTES.ACC_PS_FINAL, cacheKey: 'ACC_PS_FINAL' },
            ACC_PVC_FINAL: { url: API_ROUTES.ACC_PVC_FINAL, cacheKey: 'ACC_PVC_FINAL' },
            Forecast: { url: API_ROUTES.FORECAST, cacheKey: 'FORECAST' },
            ForecastCommentary: { url: API_ROUTES.FORECAST_COMMENTARY, cacheKey: 'FORECAST_COMMENTARY' },
            EIA_FORECAST: { url: API_ROUTES.EIA_FORECAST, cacheKey: 'EIA_FORECAST' },
            EmailBullets: { url: API_ROUTES.EMAIL_BULLETS, cacheKey: 'EMAIL_BULLETS' },
            FairPrice: {
                url: API_ROUTES.FAIR_PRICE,
                cacheKey: 'FAIR_PRICE',
                params: () => ({
                    start_date: new Date(new Date().getFullYear() - 1, 0, 1).toISOString().split('T')[0],
                    end_date: new Date().toISOString().split('T')[0],
                }),
            },
        };
        if (actionKey === 'DIESEL_PRICES')
            return this.selectiveRefresh(['DIESEL_PRICES']);
        if (actionKey === 'MI_ALL')
            return this.refreshMarketIntelAll();
        if (actionKey === 'MI_PE')
            return this.refreshMarketIntelPE();
        if (actionKey === 'MI_PP')
            return this.refreshMarketIntelPP();
        if (actionKey === 'MI_PS')
            return this.refreshMarketIntelPS();
        if (actionKey === 'MI_PVC')
            return this.refreshMarketIntelPVC();
        if (actionKey === 'MI_PET')
            return this.refreshMarketIntelPET();
        const spec = map[actionKey];
        if (!spec)
            throw new Error(`Unknown refresh action: ${actionKey}`);
        const data = await this.request({
            method: 'GET',
            url: spec.url,
            params: spec.params?.(),
        });
        if (spec.cacheKey) {
            try {
                await this.saveDataToCache(spec.cacheKey, data);
            }
            catch { /* cache write best-effort */ }
        }
        return data;
    }
    // Commentary Data Management methods
    async getDriversCommentary() {
        return this.request({
            method: "GET",
            url: API_ROUTES.COMMENTARY_DRIVERS,
        });
    }
    async createDriversCommentary(data) {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_DRIVERS_STAGING,
            data,
        });
    }
    async getPendingDriversCommentary() {
        return this.request({
            method: "GET",
            url: API_ROUTES.COMMENTARY_DRIVERS_PENDING,
        });
    }
    async commitDriversCommentaryChanges() {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_DRIVERS_COMMIT,
        });
    }
    async cancelDriversCommentaryChanges() {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_DRIVERS_CANCEL,
        });
    }
    async cancelIndividualDriverCommentaryChange(date, driverReport) {
        return this.request({
            method: "DELETE",
            url: `${API_ROUTES.COMMENTARY_DRIVERS_CANCEL}/${date}/${encodeURIComponent(driverReport)}`,
        });
    }
    async getDriversCommentaryCount() {
        return this.request({
            method: "GET",
            url: API_ROUTES.COMMENTARY_DRIVERS_COUNT,
        });
    }
    async getDriversCommentaryLatest() {
        return this.request({
            method: "GET",
            url: API_ROUTES.COMMENTARY_DRIVERS_LATEST,
        });
    }
    async getDriverPriceChanges() {
        return this.request({
            method: "GET",
            url: API_ROUTES.DRIVER_PRICE_CHANGES,
        });
    }
    async getPendingDriverPriceChanges() {
        return this.request({
            method: "GET",
            url: API_ROUTES.DRIVER_PRICE_CHANGES_PENDING,
        });
    }
    async upsertDriverPriceChange(data) {
        return this.request({
            method: "POST",
            url: API_ROUTES.DRIVER_PRICE_CHANGES,
            data,
        });
    }
    async commitDriverPriceChanges() {
        return this.request({
            method: "POST",
            url: API_ROUTES.DRIVER_PRICE_CHANGES_COMMIT,
        });
    }
    async cancelDriverPriceChanges() {
        return this.request({
            method: "POST",
            url: API_ROUTES.DRIVER_PRICE_CHANGES_CANCEL,
        });
    }
    async cancelIndividualDriverPriceChange(date) {
        return this.request({
            method: "DELETE",
            url: `${API_ROUTES.DRIVER_PRICE_CHANGES_CANCEL}/${date}`,
        });
    }
    async getPECommentary() {
        return this.request({
            method: "GET",
            url: API_ROUTES.COMMENTARY_PE,
        });
    }
    async createPECommentary(data) {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_PE,
            data,
        });
    }
    async getPECommentaryCount() {
        return this.request({
            method: "GET",
            url: API_ROUTES.COMMENTARY_PE_COUNT,
        });
    }
    async getPECommentaryLatest() {
        return this.request({
            method: "GET",
            url: API_ROUTES.COMMENTARY_PE_LATEST,
        });
    }
    async getPPCommentary() {
        return this.request({
            method: "GET",
            url: API_ROUTES.COMMENTARY_PP,
        });
    }
    async createPPCommentary(data) {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_PP,
            data,
        });
    }
    async getPPCommentaryCount() {
        return this.request({
            method: "GET",
            url: API_ROUTES.COMMENTARY_PP_COUNT,
        });
    }
    async getPPCommentaryLatest() {
        return this.request({
            method: "GET",
            url: API_ROUTES.COMMENTARY_PP_LATEST,
        });
    }
    async getPSCommentary() {
        return this.request({
            method: "GET",
            url: API_ROUTES.COMMENTARY_PS,
        });
    }
    async createPSCommentary(data) {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_PS,
            data,
        });
    }
    async getPSCommentaryCount() {
        return this.request({
            method: "GET",
            url: API_ROUTES.COMMENTARY_PS_COUNT,
        });
    }
    async getPSCommentaryLatest() {
        return this.request({
            method: "GET",
            url: API_ROUTES.COMMENTARY_PS_LATEST,
        });
    }
    async getPVCCommentary() {
        return this.request({
            method: "GET",
            url: API_ROUTES.COMMENTARY_PVC,
        });
    }
    async createPVCCommentary(data) {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_PVC,
            data,
        });
    }
    async getPVCCommentaryCount() {
        return this.request({
            method: "GET",
            url: API_ROUTES.COMMENTARY_PVC_COUNT,
        });
    }
    async getPVCCommentaryLatest() {
        return this.request({
            method: "GET",
            url: API_ROUTES.COMMENTARY_PVC_LATEST,
        });
    }
    // ==================== PE ACC Commentary - Pending Changes ====================
    async getPendingPECommentary() {
        return this.request({
            method: "GET",
            url: API_ROUTES.COMMENTARY_PE_PENDING,
        });
    }
    async commitPECommentaryChanges() {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_PE_COMMIT,
        });
    }
    async cancelPECommentaryChanges() {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_PE_CANCEL,
        });
    }
    async cancelIndividualPECommentaryChange(date) {
        return this.request({
            method: "DELETE",
            url: `${API_ROUTES.COMMENTARY_PE_CANCEL_ONE}/${date}`,
        });
    }
    async createPECommentaryFinal(data) {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_PE_FINAL,
            data,
        });
    }
    async getPendingPECommentaryFinal() {
        return this.request({
            method: "GET",
            url: API_ROUTES.COMMENTARY_PE_FINAL_PENDING,
        });
    }
    async commitPECommentaryChangesFinal() {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_PE_FINAL_COMMIT,
        });
    }
    async cancelPECommentaryChangesFinal() {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_PE_FINAL_CANCEL,
        });
    }
    async cancelIndividualPECommentaryChangeFinal(date) {
        return this.request({
            method: "DELETE",
            url: `${API_ROUTES.COMMENTARY_PE_FINAL_CANCEL}/${date}`,
        });
    }
    // ==================== PP ACC Commentary - Pending Changes ====================
    async getPendingPPCommentary() {
        return this.request({
            method: "GET",
            url: API_ROUTES.COMMENTARY_PP_PENDING,
        });
    }
    async commitPPCommentaryChanges() {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_PP_COMMIT,
        });
    }
    async cancelPPCommentaryChanges() {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_PP_CANCEL,
        });
    }
    async cancelIndividualPPCommentaryChange(date) {
        return this.request({
            method: "DELETE",
            url: `${API_ROUTES.COMMENTARY_PP_CANCEL_ONE}/${date}`,
        });
    }
    async createPPCommentaryFinal(data) {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_PP_FINAL,
            data,
        });
    }
    async getPendingPPCommentaryFinal() {
        return this.request({
            method: "GET",
            url: API_ROUTES.COMMENTARY_PP_FINAL_PENDING,
        });
    }
    async commitPPCommentaryChangesFinal() {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_PP_FINAL_COMMIT,
        });
    }
    async cancelPPCommentaryChangesFinal() {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_PP_FINAL_CANCEL,
        });
    }
    async cancelIndividualPPCommentaryChangeFinal(date) {
        return this.request({
            method: "DELETE",
            url: `${API_ROUTES.COMMENTARY_PP_FINAL_CANCEL}/${date}`,
        });
    }
    // ==================== PS ACC Commentary - Pending Changes ====================
    async getPendingPSCommentary() {
        return this.request({
            method: "GET",
            url: API_ROUTES.COMMENTARY_PS_PENDING,
        });
    }
    async commitPSCommentaryChanges() {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_PS_COMMIT,
        });
    }
    async cancelPSCommentaryChanges() {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_PS_CANCEL,
        });
    }
    async cancelIndividualPSCommentaryChange(date) {
        return this.request({
            method: "DELETE",
            url: `${API_ROUTES.COMMENTARY_PS_CANCEL_ONE}/${date}`,
        });
    }
    async createPSCommentaryFinal(data) {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_PS_FINAL,
            data,
        });
    }
    async getPendingPSCommentaryFinal() {
        return this.request({
            method: "GET",
            url: API_ROUTES.COMMENTARY_PS_FINAL_PENDING,
        });
    }
    async commitPSCommentaryChangesFinal() {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_PS_FINAL_COMMIT,
        });
    }
    async cancelPSCommentaryChangesFinal() {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_PS_FINAL_CANCEL,
        });
    }
    async cancelIndividualPSCommentaryChangeFinal(date) {
        return this.request({
            method: "DELETE",
            url: `${API_ROUTES.COMMENTARY_PS_FINAL_CANCEL}/${date}`,
        });
    }
    // ==================== PVC ACC Commentary - Pending Changes ====================
    async getPendingPVCCommentary() {
        return this.request({
            method: "GET",
            url: API_ROUTES.COMMENTARY_PVC_PENDING,
        });
    }
    async commitPVCCommentaryChanges() {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_PVC_COMMIT,
        });
    }
    async cancelPVCCommentaryChanges() {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_PVC_CANCEL,
        });
    }
    async cancelIndividualPVCCommentaryChange(date) {
        return this.request({
            method: "DELETE",
            url: `${API_ROUTES.COMMENTARY_PVC_CANCEL_ONE}/${date}`,
        });
    }
    async createPVCCommentaryFinal(data) {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_PVC_FINAL,
            data,
        });
    }
    async getPendingPVCCommentaryFinal() {
        return this.request({
            method: "GET",
            url: API_ROUTES.COMMENTARY_PVC_FINAL_PENDING,
        });
    }
    async commitPVCCommentaryChangesFinal() {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_PVC_FINAL_COMMIT,
        });
    }
    async cancelPVCCommentaryChangesFinal() {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_PVC_FINAL_CANCEL,
        });
    }
    async cancelIndividualPVCCommentaryChangeFinal(date) {
        return this.request({
            method: "DELETE",
            url: `${API_ROUTES.COMMENTARY_PVC_FINAL_CANCEL}/${date}`,
        });
    }
    // ==================== Forecast Commentary ====================
    async getForecastCommentary() {
        return this.request({
            method: "GET",
            url: API_ROUTES.FORECAST_COMMENTARY,
        });
    }
    async createForecastCommentary(data) {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_FORECAST,
            data,
        });
    }
    async getForecastCommentaryCount() {
        return this.request({
            method: "GET",
            url: API_ROUTES.COMMENTARY_FORECAST_COUNT,
        });
    }
    async getPendingForecastCommentary() {
        return this.request({
            method: "GET",
            url: API_ROUTES.COMMENTARY_FORECAST_PENDING,
        });
    }
    async commitForecastCommentaryChanges() {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_FORECAST_COMMIT,
        });
    }
    async cancelForecastCommentaryChanges() {
        return this.request({
            method: "POST",
            url: API_ROUTES.COMMENTARY_FORECAST_CANCEL,
        });
    }
    async cancelIndividualForecastCommentaryChange(forecastQtrDateId, forecastQtr, polymer) {
        return this.request({
            method: "DELETE",
            url: `${API_ROUTES.COMMENTARY_FORECAST_CANCEL}/${forecastQtrDateId}/${forecastQtr}/${polymer}`,
        });
    }
    async commitChanges(changeType) {
        return this.request({
            method: "POST",
            url: `${API_ROUTES.COMMIT_CHANGES}/${changeType}`,
        });
    }
    // General purpose request methods for flexibility
    get(url, config) {
        return this.client.get(url, config);
    }
    post(url, data, config) {
        return this.client.post(url, data, config);
    }
    put(url, data, config) {
        return this.client.put(url, data, config);
    }
    delete(url, config) {
        return this.client.delete(url, config);
    }
    // Cache-only fetchers for each data type
    async fetchCachedCaproData() {
        return this.readDataFromCache("CAPRO");
    }
    async fetchCachedPetchemData() {
        return this.readDataFromCache("PETCHEM");
    }
    async fetchCachedChlorineData() {
        return this.readDataFromCache("CHLORINE");
    }
    async fetchCachedContractsData() {
        return this.readDataFromCache("CONTRACTS");
    }
    async fetchCachedDriverData() {
        return this.readDataFromCache("DRIVERS");
    }
    async fetchCachedDriverPriceChangesData() {
        return this.readDataFromCache("DRIVER_PRICE_CHANGES");
    }
    async fetchCachedNewsData() {
        return this.readDataFromCache("NEWS_DATA");
    }
    async fetchCachedChemOrbisData() {
        return this.readDataFromCache("CHEMORBIS");
    }
    async fetchCachedPetMasterPricingData() {
        return this.readDataFromCache("PETMASTERPRICING");
    }
    async fetchCachedRefineryRatesData() {
        return this.readDataFromCache("REFINERY_RATES");
    }
    // Cache-only fetchers for ACC Monthly Snapshot data
    async fetchCachedACCPEData() {
        return this.readDataFromCache("ACC_PE");
    }
    async fetchCachedACCPPData() {
        return this.readDataFromCache("ACC_PP");
    }
    async fetchCachedACCPSData() {
        return this.readDataFromCache("ACC_PS");
    }
    async fetchCachedACCPVCData() {
        return this.readDataFromCache("ACC_PVC");
    }
    async fetchCachedACCPEDataFinal() {
        return this.readDataFromCache("ACC_PE_FINAL");
    }
    async fetchCachedACCPPDataFinal() {
        return this.readDataFromCache("ACC_PP_FINAL");
    }
    async fetchCachedACCPSDataFinal() {
        return this.readDataFromCache("ACC_PS_FINAL");
    }
    async fetchCachedACCPVCDataFinal() {
        return this.readDataFromCache("ACC_PVC_FINAL");
    }
    async fetchCachedDriverChartData() {
        return this.readDataFromCache("DRIVER_CHARTS");
    }
    async fetchCachedFairPriceData() {
        return this.readDataFromCache("FAIR_PRICE");
    }
    async fetchCachedForecastData() {
        return this.readDataFromCache("FORECAST");
    }
    async fetchCachedForecastCommentary() {
        return this.readDataFromCache("FORECAST_COMMENTARY");
    }
    async fetchCachedEiaForecastData() {
        return this.readDataFromCache("EIA_FORECAST");
    }
    async fetchCachedPEInventoryMetrics() {
        return this.readDataFromCache('MI_PE_INVENTORY');
    }
    async fetchCachedPEProductionCapacity() {
        return this.readDataFromCache('MI_PE_PRODUCTION');
    }
    async fetchCachedPEMarkets() {
        return this.readDataFromCache('MI_PE_MARKETS');
    }
    async fetchCachedPERates() {
        return this.readDataFromCache('MI_PE_RATES');
    }
    async fetchCachedPEUSITCTrade() {
        return this.readDataFromCache('MI_PE_USITC');
    }
    async fetchCachedPPRates() {
        return this.readDataFromCache('MI_PP_RATES');
    }
    async fetchCachedPPInventoryMetrics() {
        return this.readDataFromCache('MI_PP_INVENTORY');
    }
    async fetchCachedPSRates() {
        return this.readDataFromCache('MI_PS_RATES');
    }
    async fetchCachedPSInventoryMetrics() {
        return this.readDataFromCache('MI_PS_INVENTORY');
    }
    async fetchCachedPVCRates() {
        return this.readDataFromCache('MI_PVC_RATES');
    }
    async fetchCachedPVCInventoryMetrics() {
        return this.readDataFromCache('MI_PVC_INVENTORY');
    }
    async fetchCachedPETRates() {
        return this.readDataFromCache('MI_PET_RATES');
    }
    async fetchCachedPPUSITCTrade() {
        return this.readDataFromCache('MI_PP_USITC');
    }
    async fetchCachedPSUSITCTrade() {
        return this.readDataFromCache('MI_PS_USITC');
    }
    async fetchCachedPVCUSITCTrade() {
        return this.readDataFromCache('MI_PVC_USITC');
    }
    async fetchCachedPETUSITCTrade() {
        return this.readDataFromCache('MI_PET_USITC');
    }
    // Batch data refresh
    async refreshAllData() {
        const dataPromises = [
            this.fetchCaproData().catch((e) => ({
                error: "CAPRO",
                message: e.message,
            })),
            this.fetchPetchemData().catch((e) => ({
                error: "PETCHEM",
                message: e.message,
            })),
            this.fetchChlorineData().catch((e) => ({
                error: "CHLORINE",
                message: e.message,
            })),
            this.fetchDriverData().catch((e) => ({
                error: "DRIVERS",
                message: e.message,
            })),
            this.fetchChemOrbisData().catch((e) => ({
                error: "CHEMORBIS",
                message: e.message,
            })),
            this.fetchPetMasterPricingData().catch((e) => ({
                error: "PETMASTERPRICING",
                message: e.message,
            })),
            this.fetchContractsData().catch((e) => ({
                error: "CONTRACTS",
                message: e.message,
            })),
            this.fetchRefineryRatesData().catch((e) => ({
                error: "REFINERYRATES",
                message: e.message,
            })),
            this.fetchDriverChartData().catch((e) => ({
                error: "DRIVERCHARTS",
                message: e.message,
            })),
            this.fetchFairPriceData().catch((e) => ({
                error: "FAIR_PRICE",
                message: e.message,
            })),
            this.fetchForecastData().catch((e) => ({
                error: "FORECAST",
                message: e.message,
            })),
            this.fetchForecastCommentary().catch((e) => ({
                error: "FORECAST_COMMENTARY",
                message: e.message,
            })),
            this.fetchACCPEData().catch((e) => ({
                error: "ACC_PE",
                message: e.message,
            })),
            this.fetchACCPPData().catch((e) => ({
                error: "ACC_PP",
                message: e.message,
            })),
            this.fetchACCPSData().catch((e) => ({
                error: "ACC_PS",
                message: e.message,
            })),
            this.fetchACCPVCData().catch((e) => ({
                error: "ACC_PVC",
                message: e.message,
            })),
            this.fetchEiaForecastData().catch((e) => ({
                error: "EIA_FORECAST",
                message: e.message,
            })),
        ];
        const results = await Promise.allSettled(dataPromises);
        const data = { errors: [] };
        results.forEach((result, index) => {
            if (result.status === "fulfilled") {
                if ("error" in result.value) {
                    data.errors.push(`${result.value.error}: ${result.value.message}`);
                }
                else {
                    const key = Object.keys(DATA_FILES)[index];
                    data[key] = result.value;
                }
            }
            else {
                data.errors.push(`Request ${index + 1} failed: ${result.reason}`);
            }
        });
        return data;
    }
    // Utility methods
    setBaseUrl(url) {
        this.baseUrl = url;
        this.client.defaults.baseURL = url;
    }
    // Must set the adapter on this instance, not axios.defaults: axios.create()
    // copies adapter into the instance defaults, and those always win.
    setAdapter(adapter) {
        this.client.defaults.adapter = adapter;
    }
    getAdapter() {
        return this.client.defaults.adapter;
    }
    getBaseUrl(customUrl) {
        if (customUrl)
            return customUrl;
        // Dynamic base URL logic extracted from AngularJS
        const hostname = typeof window !== "undefined" ? window.location.hostname : "";
        if (hostname.includes("uat")) {
            return "https://uat.resinsmart.ai";
        }
        else if (hostname.includes("app")) {
            return "https://app.resinsmart.ai";
        }
        else if (hostname.includes("dev")) {
            return "https://dev.resinsmart.ai";
        }
        else {
            return "https://dev.resinsmart.ai"; // Default to dev
        }
    }
    setupInterceptors() {
        this.client.interceptors.request.use((config) => {
            const token = this.getAccessToken();
            // Only add Authorization header if not logging in or signing up free trial
            if (token && config.url !== API_ROUTES.AUTHORIZE && config.url !== API_ROUTES.FREE_TRIAL_SIGNUP) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        }, (error) => Promise.reject(error));
        // Response interceptor for error handling
        this.client.interceptors.response.use((response) => response, async (error) => {
            // Check if this request should skip auto-redirect on 401
            const skipAuthRedirect = error.config?.skipAuthRedirect;
            if (error.response?.status === 401) {
                // Handle token refresh or redirect to login (unless skipped)
                if (!skipAuthRedirect) {
                    this.handleUnauthorized();
                }
            }
            else if (error.response?.status === 403) {
                console.warn("403 Forbidden - Session invalidated. Logging out...");
                if (typeof window !== "undefined") {
                    if (!window.__logoutTriggered) {
                        window.__logoutTriggered = true;
                        localStorage.removeItem("access_token");
                        localStorage.removeItem("user");
                        localStorage.removeItem("last_activity");
                        window.dispatchEvent(new Event("auth-logout"));
                        if (!window.location.pathname.includes("/authorization")) {
                            window.location.href = "/authorization";
                        }
                        setTimeout(() => {
                            window.__logoutTriggered = false;
                        }, 2000);
                    }
                }
            }
            return Promise.reject(error);
        });
    }
    getAccessToken() {
        if (typeof window !== "undefined") {
            return (localStorage.getItem("access_token") || this.getCookie("access_token"));
        }
        return null;
    }
    getCookie(name) {
        if (typeof document !== "undefined") {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; ${name}=`);
            if (parts.length === 2)
                return parts.pop()?.split(";").shift() || null;
        }
        return null;
    }
    handleUnauthorized() {
        // Clear tokens and redirect to login
        if (typeof window !== "undefined") {
            localStorage.removeItem("access_token");
            document.cookie =
                "access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
            // Also clear user data to immediately invalidate auth state
            localStorage.removeItem("user");
            localStorage.removeItem("last_activity");
            // Dispatch a custom event to notify components of logout
            window.dispatchEvent(new Event('auth-logout'));
            window.location.href = "/authorization";
        }
    }
    // Generic request method
    async request(config) {
        try {
            const response = await this.client.request(config);
            return response.data;
        }
        catch (error) {
            console.error("API request failed:", error);
            throw error;
        }
    }
    async requestPasswordReset(email) {
        const resp = await this.request({
            method: "POST",
            url: API_ROUTES.PASSWORD_RESET_REQUEST,
            data: { email },
        });
        return !!resp;
    }
    async verifyPasswordReset(token, email) {
        return this.request({
            method: "POST",
            url: API_ROUTES.PASSWORD_RESET_VERIFY,
            data: { token, email },
        });
    }
    async completePasswordReset(token, email, new_password) {
        const resp = await this.request({
            method: "POST",
            url: API_ROUTES.PASSWORD_RESET_COMPLETE,
            data: { token, email, new_password },
        });
        return !!resp;
    }
    // User Management API Functions
    async getAllUsers() {
        return this.request({
            method: "GET",
            url: API_ROUTES.USER_ALL,
        });
    }
    async impersonateUser(userId) {
        return this.request({
            method: "GET",
            url: `${API_ROUTES.USER_IMPERSONATE}/${userId}`,
        });
    }
    async getAdminUsers() {
        const users = await this.getAllUsers();
        return users.filter(user => user.is_admin && user.is_active);
    }
    async createUser(user) {
        return this.request({
            method: "POST",
            url: API_ROUTES.USER_ROOT,
            data: { user },
        });
    }
    async updateUser(userId, user) {
        return this.request({
            method: "PUT",
            url: API_ROUTES.USER_ROOT,
            data: { user },
            params: { user_id: userId },
        });
    }
    async deleteUser(userId) {
        return this.request({
            method: "DELETE",
            url: API_ROUTES.USER_ROOT,
            params: { user_id: userId },
        });
    }
    async adminChangeUserPassword(userId, newPassword) {
        return this.request({
            method: 'PUT',
            url: API_ROUTES.ADMIN_RESET_PASSWORD,
            data: { new_password: newPassword },
            params: { user_id: userId },
        });
    }
    // Get cache version from server
    async getCacheVersion() {
        const response = await this.request({
            method: 'GET',
            url: API_ROUTES.CACHE_VERSION,
        });
        return response.version;
    }
    async getUserActivity(email) {
        return await this.request({
            method: 'GET',
            url: `${API_ROUTES.USER_ACTIVITY}/${email}`
        });
    }
    async postUserActivity(activity) {
        return await this.request({
            method: 'POST',
            url: API_ROUTES.USER_ACTIVITY,
            data: activity
        });
    }
    async billingPlans() {
        return this.request({ method: 'GET', url: API_ROUTES.BILLING_PLANS });
    }
    async billingCheckout(userId, productId, returnUrl, promo) {
        return this.request({
            method: 'POST',
            url: API_ROUTES.BILLING_CHECKOUT,
            data: {
                user_id: userId,
                product_id: productId,
                return_url: returnUrl,
                market_link_id: promo?.marketLinkId,
                promo_code: promo?.promoCode,
            },
        });
    }
    async billingValidatePromoCode(code, membershipId) {
        return this.request({
            method: 'POST',
            url: API_ROUTES.BILLING_PROMO_VALIDATE,
            data: { code, membership_id: membershipId },
        });
    }
    async billingSubscription(userId) {
        return this.request({ method: 'GET', url: `${API_ROUTES.BILLING_SUBSCRIPTION}/${userId}` });
    }
    async billingSubscriptionSync(userId) {
        return this.request({ method: 'POST', url: `${API_ROUTES.BILLING_SUBSCRIPTION_SYNC}/${userId}` });
    }
    async billingCheckoutConfirm(userId) {
        return this.request({ method: 'POST', url: API_ROUTES.BILLING_CHECKOUT_CONFIRM, data: { user_id: userId } });
    }
    // ── Admin: Subscriptions (Employee + Admin only) ──────────────────────────
    async adminGetSubscriptions() {
        return this.request({ method: 'GET', url: API_ROUTES.ADMIN_SUBSCRIPTIONS });
    }
    async adminGetSubscriptionAudit(companyId) {
        return this.request({
            method: 'GET',
            url: API_ROUTES.ADMIN_SUBSCRIPTION_AUDIT,
            params: companyId !== undefined ? { company_id: companyId } : undefined,
        });
    }
    async billingCancel(userId) {
        return this.request({ method: 'POST', url: API_ROUTES.BILLING_SUBSCRIPTION_CANCEL, data: { user_id: userId } });
    }
    async billingPause(userId) {
        return this.request({ method: 'POST', url: API_ROUTES.BILLING_SUBSCRIPTION_PAUSE, data: { user_id: userId } });
    }
    async billingResume(userId) {
        return this.request({ method: 'POST', url: API_ROUTES.BILLING_SUBSCRIPTION_RESUME, data: { user_id: userId } });
    }
    async billingInvoices(userId) {
        return this.request({ method: 'GET', url: `${API_ROUTES.BILLING_INVOICES}/${userId}` });
    }
    async billingUpdateCardLink(userId) {
        return this.request({ method: 'POST', url: API_ROUTES.BILLING_UPDATE_CARD_LINK, data: { user_id: userId } });
    }
    async billingContactSales(payload) {
        return this.request({ method: 'POST', url: API_ROUTES.BILLING_CONTACT, data: payload });
    }
    async checkCachedPdfExists(filename) {
        return this.request({
            method: 'GET',
            url: API_ROUTES.DRIVERS_PDF_CHECK_CACHE,
            params: { filename },
        });
    }
    async downloadCachedPdf(filename) {
        const response = await this.client.get(API_ROUTES.DRIVERS_PDF_DOWNLOAD, {
            params: { filename },
            responseType: 'blob',
        });
        return response.data;
    }
    async saveDriverPdf(pdfBlob, filename, date) {
        const formData = new FormData();
        formData.append('file', pdfBlob, filename);
        formData.append('date', date);
        const response = await this.client.post(API_ROUTES.DRIVERS_PDF_SAVE, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data === true;
    }
    async checkPetchemPdfExists(filename) {
        return this.request({
            method: 'GET',
            url: API_ROUTES.PETCHEM_FILE_CHECK_CACHE,
            params: { filename },
        });
    }
    async downloadPetchemPdf(filename) {
        const response = await this.client.get(API_ROUTES.PETCHEM_FILE_DOWNLOAD, {
            params: { filename },
            responseType: 'blob',
        });
        return response.data;
    }
    async savePetchemPdf(pdfBlob, filename) {
        const formData = new FormData();
        formData.append('file', pdfBlob, filename);
        const response = await this.client.post(API_ROUTES.PETCHEM_FILE_SAVE, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data === true;
    }
    async checkAccPdfExists(filename) {
        return this.request({
            method: 'GET',
            url: API_ROUTES.ACC_PDF_CHECK_CACHE,
            params: { filename },
        });
    }
    async downloadAccPdf(filename) {
        const response = await this.client.get(API_ROUTES.ACC_PDF_DOWNLOAD, {
            params: { filename },
            responseType: 'blob',
        });
        return response.data;
    }
    async saveAccPdf(pdfBlob, filename) {
        const formData = new FormData();
        formData.append('file', pdfBlob, filename);
        const response = await this.client.post(API_ROUTES.ACC_PDF_SAVE, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data === true;
    }
    // ── Company ──────────────────────────────────────────────────────────────
    async getCompany() {
        return this.request({ method: 'GET', url: API_ROUTES.COMPANY });
    }
    async updateCompany(data) {
        return this.request({ method: 'PUT', url: API_ROUTES.COMPANY, data });
    }
    async getCompanyMembers() {
        return this.request({ method: 'GET', url: API_ROUTES.COMPANY_MEMBERS });
    }
    async addCompanyMember(email) {
        return this.request({ method: 'POST', url: API_ROUTES.COMPANY_MEMBERS, data: { email } });
    }
    async removeCompanyMember(userId) {
        return this.request({ method: 'DELETE', url: `${API_ROUTES.COMPANY_MEMBERS}/${userId}` });
    }
    async updateMemberRole(userId, role) {
        return this.request({ method: 'PUT', url: `${API_ROUTES.COMPANY_MEMBERS}/${userId}/role`, data: { role } });
    }
    async approveMember(userId) {
        return this.request({ method: 'POST', url: `${API_ROUTES.COMPANY_MEMBERS}/${userId}/approve` });
    }
    async rejectMember(userId) {
        return this.request({ method: 'POST', url: `${API_ROUTES.COMPANY_MEMBERS}/${userId}/reject` });
    }
    async getCompanyDomains() {
        return this.request({ method: 'GET', url: API_ROUTES.COMPANY_DOMAINS });
    }
    async claimDomain(domain) {
        return this.request({ method: 'POST', url: API_ROUTES.COMPANY_DOMAINS, data: { domain } });
    }
    async startDomainVerification(domainId, method, email) {
        return this.request({ method: 'POST', url: `${API_ROUTES.COMPANY_DOMAINS}/${domainId}/verify`, data: { method, email } });
    }
    async checkDomainVerification(domainId) {
        return this.request({ method: 'GET', url: `${API_ROUTES.COMPANY_DOMAINS}/${domainId}/check` });
    }
    async removeDomain(domainId) {
        return this.request({ method: 'DELETE', url: `${API_ROUTES.COMPANY_DOMAINS}/${domainId}` });
    }
    // ── Admin: System Tickets (Azure DevOps) ─────────────────────────────────
    async devOpsConfig() {
        return this.request({ method: 'GET', url: API_ROUTES.DEVOPS_CONFIG });
    }
    async devOpsListAssignees() {
        return this.request({ method: 'GET', url: API_ROUTES.DEVOPS_ASSIGNEES });
    }
    async devOpsListRequesters() {
        return this.request({ method: 'GET', url: API_ROUTES.DEVOPS_REQUESTERS });
    }
    async devOpsListEpics() {
        return this.request({ method: 'GET', url: API_ROUTES.DEVOPS_EPICS });
    }
    async devOpsListSprints() {
        return this.request({ method: 'GET', url: API_ROUTES.DEVOPS_SPRINTS });
    }
    async devOpsCreateSprint(payload) {
        return this.request({ method: 'POST', url: API_ROUTES.DEVOPS_SPRINTS, data: payload });
    }
    async devOpsUpdateSprint(name, payload) {
        return this.request({ method: 'PATCH', url: `${API_ROUTES.DEVOPS_SPRINTS}/${encodeURIComponent(name)}`, data: payload });
    }
    async devOpsListTickets(params = {}) {
        return this.request({ method: 'GET', url: API_ROUTES.DEVOPS_TICKETS, params });
    }
    async devOpsGetTicket(workItemId) {
        return this.request({ method: 'GET', url: `${API_ROUTES.DEVOPS_TICKETS}/${workItemId}` });
    }
    async devOpsCreateTicket(data) {
        return this.request({ method: 'POST', url: API_ROUTES.DEVOPS_TICKETS, data });
    }
    async devOpsUpdateTicket(workItemId, data) {
        return this.request({ method: 'PATCH', url: `${API_ROUTES.DEVOPS_TICKETS}/${workItemId}`, data });
    }
    async devOpsTakeTicket(workItemId) {
        return this.request({ method: 'POST', url: `${API_ROUTES.DEVOPS_TICKETS}/${workItemId}/take` });
    }
    async devOpsDeleteTicket(workItemId) {
        return this.request({ method: 'DELETE', url: `${API_ROUTES.DEVOPS_TICKETS}/${workItemId}` });
    }
    async devOpsListComments(workItemId) {
        return this.request({ method: 'GET', url: `${API_ROUTES.DEVOPS_TICKETS}/${workItemId}/comments` });
    }
    async devOpsAddComment(workItemId, data) {
        return this.request({ method: 'POST', url: `${API_ROUTES.DEVOPS_TICKETS}/${workItemId}/comments`, data });
    }
    async devOpsUploadAttachment(file) {
        const formData = new FormData();
        formData.append('file', file, file.name);
        const response = await this.client.post(API_ROUTES.DEVOPS_ATTACHMENTS, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    }
    async devOpsGetAttachment(path) {
        const response = await this.client.get(path, { responseType: 'blob' });
        return response.data;
    }
    // ── Admin: Companies ─────────────────────────────────────────────────────
    async adminListCompanies() {
        return this.request({ method: 'GET', url: API_ROUTES.ADMIN_COMPANIES });
    }
    async adminGetCompany(companyId) {
        return this.request({ method: 'GET', url: `${API_ROUTES.ADMIN_COMPANIES}/${companyId}` });
    }
    async adminCreateCompany(data) {
        return this.request({ method: 'POST', url: API_ROUTES.ADMIN_COMPANIES, data });
    }
    async adminUpdateCompany(companyId, data) {
        return this.request({ method: 'PUT', url: `${API_ROUTES.ADMIN_COMPANIES}/${companyId}`, data });
    }
    async adminMergeCompanies(targetId, sourceCompanyId) {
        return this.request({ method: 'POST', url: `${API_ROUTES.ADMIN_COMPANIES}/${targetId}/merge`, data: { source_company_id: sourceCompanyId } });
    }
    async adminSetMemberRole(companyId, userId, role) {
        return this.request({
            method: 'PUT',
            url: `${API_ROUTES.ADMIN_COMPANIES}/${companyId}/members/${userId}/role`,
            data: { role },
        });
    }
    async adminDeleteCompany(companyId) {
        return this.request({
            method: 'DELETE',
            url: `${API_ROUTES.ADMIN_COMPANIES}/${companyId}`,
        });
    }
    async adminListDeletedCompanies() {
        return this.request({
            method: 'GET',
            url: `${API_ROUTES.ADMIN_COMPANIES}/deleted`,
        });
    }
    async adminRestoreCompany(companyId) {
        return this.request({
            method: 'POST',
            url: `${API_ROUTES.ADMIN_COMPANIES}/deleted/${companyId}/restore`,
        });
    }
    async adminListEmptyCompanies() {
        return this.request({
            method: 'GET',
            url: `${API_ROUTES.ADMIN_COMPANIES}/empty`,
        });
    }
    async adminCleanupEmptyCompanies() {
        return this.request({
            method: 'POST',
            url: `${API_ROUTES.ADMIN_COMPANIES}/cleanup-empty`,
        });
    }
    async adminGetCompanyDomains(companyId) {
        return this.request({ method: 'GET', url: `${API_ROUTES.ADMIN_COMPANIES}/${companyId}/domains` });
    }
    async adminAddCompanyDomain(companyId, domain) {
        return this.request({ method: 'POST', url: `${API_ROUTES.ADMIN_COMPANIES}/${companyId}/domains`, data: { domain } });
    }
    async adminDeleteCompanyDomain(companyId, domainId) {
        return this.request({ method: 'DELETE', url: `${API_ROUTES.ADMIN_COMPANIES}/${companyId}/domains/${domainId}` });
    }
    // ── Admin: Parent Groups ─────────────────────────────────────────────────
    async adminListParentGroups() {
        return this.request({ method: 'GET', url: API_ROUTES.ADMIN_PARENT_GROUPS });
    }
    async adminCreateParentGroup(data) {
        return this.request({ method: 'POST', url: API_ROUTES.ADMIN_PARENT_GROUPS, data });
    }
    async adminUpdateParentGroup(groupId, data) {
        return this.request({ method: 'PUT', url: `${API_ROUTES.ADMIN_PARENT_GROUPS}/${groupId}`, data });
    }
    async adminAddCompanyToGroup(groupId, companyId) {
        return this.request({ method: 'POST', url: `${API_ROUTES.ADMIN_PARENT_GROUPS}/${groupId}/companies`, data: { company_id: companyId } });
    }
    async adminRemoveCompanyFromGroup(groupId, companyId) {
        return this.request({ method: 'DELETE', url: `${API_ROUTES.ADMIN_PARENT_GROUPS}/${groupId}/companies/${companyId}` });
    }
    async adminListGroupMembers(groupId) {
        return this.request({ method: 'GET', url: `${API_ROUTES.ADMIN_PARENT_GROUPS}/${groupId}/members` });
    }
    async adminAddGroupMember(groupId, userId, role) {
        return this.request({ method: 'POST', url: `${API_ROUTES.ADMIN_PARENT_GROUPS}/${groupId}/members`, data: { user_id: userId, role } });
    }
    async adminUpdateGroupMemberRole(groupId, userId, role) {
        return this.request({ method: 'PUT', url: `${API_ROUTES.ADMIN_PARENT_GROUPS}/${groupId}/members/${userId}/role`, data: { role } });
    }
    async adminRemoveGroupMember(groupId, userId) {
        return this.request({ method: 'DELETE', url: `${API_ROUTES.ADMIN_PARENT_GROUPS}/${groupId}/members/${userId}` });
    }
    async getFeedsState() {
        return this.request({ method: 'GET', url: API_ROUTES.FEEDS_STATE });
    }
    async triggerFeedEmail(feedName) {
        return this.request({ method: 'POST', url: `${API_ROUTES.FEEDS_TRIGGER}/${feedName}` });
    }
    async previewFeedEmail(feedName) {
        return this.request({ method: 'GET', url: `${API_ROUTES.FEEDS_PREVIEW}/${feedName}` });
    }
    async testSendFeedEmail(feedName, email) {
        return this.request({ method: 'POST', url: `${API_ROUTES.FEEDS_TEST_SEND}/${feedName}`, data: { email } });
    }
    async refreshRssFeeds() {
        return this.request({ method: 'POST', url: API_ROUTES.FEEDS_RSS_REFRESH });
    }
    async refreshSingleRssFeed(feedName) {
        return this.request({ method: 'POST', url: `${API_ROUTES.FEEDS_REFRESH_RSS_SINGLE}/${feedName}` });
    }
    // ── User: Notifications ───────────────────────────────────────────────
    async getNotifications() {
        return this.request({
            method: 'GET',
            url: API_ROUTES.USER_NOTIFICATIONS
        });
    }
    async updateNotificationSubscription(subscriptionKey, enabled) {
        return this.request({
            method: 'PATCH',
            url: `${API_ROUTES.USER_NOTIFICATIONS}/${subscriptionKey}`,
            data: { enabled }
        });
    }
    async syncHubSpotSubscriptions() {
        return this.request({
            method: 'POST',
            url: API_ROUTES.USER_NOTIFICATIONS_SYNC
        });
    }
    async getNotificationAudit(limit = 50) {
        return this.request({
            method: 'GET',
            url: `${API_ROUTES.USER_NOTIFICATIONS_AUDIT}?limit=${limit}`
        });
    }
    async getUserDataNotificationPreferences() {
        return this.request({ method: 'GET', url: API_ROUTES.USER_DATA_NOTIFICATIONS });
    }
    async updateDataNotificationPreference(channelKey, body) {
        return this.request({ method: 'PUT', url: `${API_ROUTES.USER_DATA_NOTIFICATIONS}/${channelKey}`, data: body });
    }
    async getInAppNotifications() {
        return this.request({ method: 'GET', url: API_ROUTES.USER_DATA_NOTIFICATIONS_INBOX });
    }
    async getUnreadNotificationCount() {
        return this.request({ method: 'GET', url: `${API_ROUTES.USER_DATA_NOTIFICATIONS_INBOX}/unread-count` });
    }
    async markNotificationRead(notificationId) {
        return this.request({ method: 'POST', url: `${API_ROUTES.USER_DATA_NOTIFICATIONS_INBOX}/${notificationId}/read` });
    }
    async markAllNotificationsRead() {
        return this.request({ method: 'POST', url: `${API_ROUTES.USER_DATA_NOTIFICATIONS_INBOX}/read-all` });
    }
    async deleteNotification(notificationId) {
        return this.request({ method: 'DELETE', url: `${API_ROUTES.USER_DATA_NOTIFICATIONS_INBOX}/${notificationId}` });
    }
    async deleteAllNotifications() {
        return this.request({ method: 'DELETE', url: API_ROUTES.USER_DATA_NOTIFICATIONS_INBOX });
    }
    async adminSyncAllHubSpot() {
        return this.request({
            method: 'POST',
            url: API_ROUTES.ADMIN_SYNC_ALL_HUBSPOT
        });
    }
    async adminStartHubSpotIdScan() {
        return this.request({ method: 'POST', url: API_ROUTES.ADMIN_HUBSPOT_ID_SCAN });
    }
    async adminGetHubSpotIdScan() {
        return this.request({ method: 'GET', url: API_ROUTES.ADMIN_HUBSPOT_ID_SCAN });
    }
    async adminApplyHubSpotIds(links) {
        return this.request({ method: 'POST', url: API_ROUTES.ADMIN_HUBSPOT_ID_APPLY, data: { links } });
    }
    async adminGetHubSpotSyncStatus() {
        return this.request({
            method: 'GET',
            url: API_ROUTES.ADMIN_SYNC_ALL_HUBSPOT_STATUS
        });
    }
    // ── Admin: Available Subscriptions ────────────────────────────────────────
    async adminGetAvailableSubscriptions() {
        return this.request({ method: 'GET', url: API_ROUTES.ADMIN_AVAILABLE_SUBSCRIPTIONS });
    }
    async adminCreateAvailableSubscription(data) {
        return this.request({ method: 'POST', url: API_ROUTES.ADMIN_AVAILABLE_SUBSCRIPTIONS, data });
    }
    async adminUpdateAvailableSubscription(id, data) {
        return this.request({ method: 'PATCH', url: `${API_ROUTES.ADMIN_AVAILABLE_SUBSCRIPTIONS}/${id}`, data });
    }
    async adminDeleteAvailableSubscription(id) {
        return this.request({ method: 'DELETE', url: `${API_ROUTES.ADMIN_AVAILABLE_SUBSCRIPTIONS}/${id}` });
    }
    // ── Admin: Market Links ───────────────────────────────────────────────────
    async adminGetMarketLinks() {
        return this.request({ method: 'GET', url: API_ROUTES.ADMIN_MARKET_LINKS });
    }
    async adminCreateMarketLink(data) {
        return this.request({ method: 'POST', url: API_ROUTES.ADMIN_MARKET_LINKS, data });
    }
    async adminUpdateMarketLink(id, data) {
        return this.request({ method: 'PATCH', url: `${API_ROUTES.ADMIN_MARKET_LINKS}/${id}`, data });
    }
    async adminDeleteMarketLink(id) {
        return this.request({ method: 'DELETE', url: `${API_ROUTES.ADMIN_MARKET_LINKS}/${id}` });
    }
    // ── Admin: Promo Codes ────────────────────────────────────────────────────
    async adminGetPromoCodes() {
        return this.request({ method: 'GET', url: API_ROUTES.ADMIN_PROMO_CODES });
    }
    async adminCreatePromoCode(data) {
        return this.request({ method: 'POST', url: API_ROUTES.ADMIN_PROMO_CODES, data });
    }
    async adminUpdatePromoCode(id, data) {
        return this.request({ method: 'PATCH', url: `${API_ROUTES.ADMIN_PROMO_CODES}/${id}`, data });
    }
    async adminDeletePromoCode(id) {
        return this.request({ method: 'DELETE', url: `${API_ROUTES.ADMIN_PROMO_CODES}/${id}` });
    }
    async adminGetPromoCodeRedemptions(id) {
        return this.request({ method: 'GET', url: `${API_ROUTES.ADMIN_PROMO_CODES}/${id}/redemptions` });
    }
    async adminTestNotification(data) {
        return this.request({ method: 'POST', url: API_ROUTES.ADMIN_TEST_NOTIFICATION, data });
    }
    async getFeatureFlags(impersonateUserId) {
        const url = impersonateUserId
            ? `${API_ROUTES.FEATURE_FLAGS}?impersonate_user_id=${impersonateUserId}`
            : API_ROUTES.FEATURE_FLAGS;
        return this.request({ method: 'GET', url });
    }
    async adminGetFeatureFlags() {
        return this.request({ method: 'GET', url: API_ROUTES.ADMIN_FEATURE_FLAGS });
    }
    /** Metadata for the active demo snapshot. Admin only. */
    async getDemoSnapshotMeta() {
        return this.request({
            method: 'GET',
            url: API_ROUTES.DEMO_ADMIN_SNAPSHOT,
        });
    }
    /** Upload a captured bundle and make it the active snapshot. Admin only. */
    async uploadDemoSnapshot(bundle, label) {
        return this.request({
            method: 'POST',
            url: API_ROUTES.DEMO_ADMIN_CAPTURE,
            data: { bundle, label },
        });
    }
    async getDemoAnalytics(months = 12, period) {
        return this.request({
            method: 'GET',
            url: API_ROUTES.DEMO_ADMIN_ANALYTICS,
            params: { months, period },
        });
    }
    async getAnalyticsConfig() {
        return this.request({ method: 'GET', url: API_ROUTES.ANALYTICS_CONFIG });
    }
    async getAnalyticsOverview(days = 30, refresh = false) {
        return this.request({
            method: 'GET',
            url: API_ROUTES.ANALYTICS_OVERVIEW,
            params: refresh ? { days, refresh: true } : { days },
        });
    }
    async getClientHealth(refresh = false) {
        return this.request({
            method: 'GET',
            url: API_ROUTES.ANALYTICS_CLIENT_HEALTH,
            params: refresh ? { refresh: true } : undefined,
        });
    }
    async adminCreateFeatureFlag(data) {
        return this.request({ method: 'POST', url: API_ROUTES.ADMIN_FEATURE_FLAGS, data });
    }
    async adminUpdateFeatureFlag(featureKey, data) {
        return this.request({ method: 'PUT', url: `${API_ROUTES.ADMIN_FEATURE_FLAGS}/${featureKey}`, data });
    }
    async adminDeleteFeatureFlag(featureKey) {
        return this.request({ method: 'DELETE', url: `${API_ROUTES.ADMIN_FEATURE_FLAGS}/${featureKey}` });
    }
    async getNavItems() {
        return this.request({ method: 'GET', url: API_ROUTES.NAV_ITEMS });
    }
    async adminGetNavItems() {
        return this.request({ method: 'GET', url: API_ROUTES.ADMIN_NAV_ITEMS });
    }
    async adminUpdateNavItem(navKey, data) {
        return this.request({ method: 'PUT', url: `${API_ROUTES.ADMIN_NAV_ITEMS}/${navKey}`, data });
    }
    async adminCreateNavItem(data) {
        return this.request({ method: 'POST', url: API_ROUTES.ADMIN_NAV_ITEMS, data });
    }
    async adminDeleteNavItem(navKey) {
        return this.request({ method: 'DELETE', url: `${API_ROUTES.ADMIN_NAV_ITEMS}/${navKey}` });
    }
    async getClientData(company) {
        return this.request({
            method: 'GET',
            url: `${API_ROUTES.CLIENT_DATA}/${company}`,
        });
    }
    // ── Email Bullets ─────────────────────────────────────────────────────────
    async getEmailBullets() {
        return this.request({ method: 'GET', url: API_ROUTES.EMAIL_BULLETS });
    }
    async getEmailBulletById(id) {
        return this.request({ method: 'GET', url: `${API_ROUTES.EMAIL_BULLETS}/${id}` });
    }
    async createEmailBullet(payload) {
        return this.request({ method: 'POST', url: API_ROUTES.EMAIL_BULLETS, data: payload });
    }
    async updateEmailBullet(id, payload) {
        return this.request({ method: 'PUT', url: `${API_ROUTES.EMAIL_BULLETS}/${id}`, data: payload });
    }
    async deleteEmailBullet(id) {
        return this.request({ method: 'DELETE', url: `${API_ROUTES.EMAIL_BULLETS}/${id}` });
    }
    // Staging bullets
    async getStagingBullets() {
        return this.request({ method: 'GET', url: `${API_ROUTES.EMAIL_BULLETS}/staging` });
    }
    async createStagingBullet(payload) {
        return this.request({ method: 'POST', url: `${API_ROUTES.EMAIL_BULLETS}/staging`, data: payload });
    }
    async updateStagingBullet(id, payload) {
        return this.request({ method: 'PUT', url: `${API_ROUTES.EMAIL_BULLETS}/staging/${id}`, data: payload });
    }
    async deleteStagingBullet(id) {
        return this.request({ method: 'DELETE', url: `${API_ROUTES.EMAIL_BULLETS}/staging/${id}` });
    }
    async publishStagingBullets() {
        return this.request({ method: 'POST', url: `${API_ROUTES.EMAIL_BULLETS}/staging/publish` });
    }
    // ── Freightos ─────────────────────────────────────────────────────────
    async getFreightosData(params) {
        const cached = await this.readDataFromCache('FREIGHTOS');
        if (cached)
            return cached;
        const data = await this.request({ method: 'GET', url: API_ROUTES.FREIGHTOS, params });
        await this.saveDataToCache('FREIGHTOS', data);
        return data;
    }
    async getSnapshotAISummary(polymer, type, force) {
        return this.request({
            method: 'GET',
            url: `${API_ROUTES.AI_SNAPSHOT_SUMMARY}/${polymer.toLowerCase()}`,
            params: { type, ...(force ? { force: true } : {}) },
        });
    }
    async getFreightAIInsights(tradelane, mode, period, force) {
        return this.request({
            method: 'GET',
            url: `${API_ROUTES.AI_INSIGHTS}/freight-rates`,
            params: { tradelane, mode, period, ...(force ? { force: true } : {}) },
        });
    }
    async regenerateFreightAIInsights(tradelane, mode, period, overrides) {
        return this.request({
            method: 'POST',
            url: `${API_ROUTES.AI_INSIGHTS}/freight-rates/regenerate`,
            data: { tradelane, mode, period, ...overrides },
        });
    }
    async getAIInsights(context, force) {
        return this.request({
            method: 'GET',
            url: `${API_ROUTES.AI_INSIGHTS}/${context}`,
            params: force ? { force: true } : undefined,
        });
    }
    async getAiPromptTemplate(context) {
        return this.request({
            method: 'GET',
            url: `${API_ROUTES.AI_PROMPT_TEMPLATE}/${context}`,
        });
    }
    async saveAiPromptTemplate(context, template) {
        return this.request({
            method: 'PUT',
            url: `${API_ROUTES.AI_PROMPT_TEMPLATE}/${context}`,
            data: template,
        });
    }
    async regenerateAiInsights(context, overrides) {
        return this.request({
            method: 'POST',
            url: `${API_ROUTES.AI_INSIGHTS_REGENERATE}/${context}/regenerate`,
            data: overrides,
        });
    }
    async regenerateAiSummary(polymer, type, overrides) {
        return this.request({
            method: 'POST',
            url: `${API_ROUTES.AI_SUMMARY_REGENERATE}/${polymer.toLowerCase()}/regenerate`,
            data: { type, ...overrides },
        });
    }
    async getAdminMenuPreferences() {
        const setting = await this.request({
            method: 'GET',
            url: API_ROUTES.USER_ADMIN_MENU,
        });
        const items = Array.isArray(setting.payload?.items) ? setting.payload.items : [];
        return { items, etag: setting.etag };
    }
    async updateAdminMenuPreferences(items, etag) {
        const setting = await this.request({
            method: 'PATCH',
            url: API_ROUTES.USER_ADMIN_MENU,
            data: { items, etag: etag ?? null },
        });
        const next = Array.isArray(setting.payload?.items) ? setting.payload.items : [];
        return { items: next, etag: setting.etag };
    }
    // ─────────── Daily Spot & Contracts Data Entry ───────────
    async getDailySpotHistory(limit = 90) {
        return this.request({ method: 'GET', url: `${API_ROUTES.DAILY_SPOT}?limit=${limit}` });
    }
    async upsertDailySpotEntry(data) {
        return this.request({ method: 'POST', url: API_ROUTES.DAILY_SPOT, data });
    }
    async getPendingDailySpot() {
        return this.request({ method: 'GET', url: API_ROUTES.DAILY_SPOT_PENDING });
    }
    async commitDailySpot() {
        return this.request({ method: 'POST', url: API_ROUTES.DAILY_SPOT_COMMIT });
    }
    async cancelAllDailySpot() {
        return this.request({ method: 'POST', url: API_ROUTES.DAILY_SPOT_CANCEL });
    }
    async cancelOneDailySpot(date) {
        return this.request({ method: 'DELETE', url: `${API_ROUTES.DAILY_SPOT_CANCEL}/${date}` });
    }
    async upsertDailyContractsEntry(data) {
        return this.request({ method: 'POST', url: API_ROUTES.DAILY_CONTRACTS, data });
    }
    async getPendingDailyContracts() {
        return this.request({ method: 'GET', url: API_ROUTES.DAILY_CONTRACTS_PENDING });
    }
    async commitDailyContracts() {
        return this.request({ method: 'POST', url: API_ROUTES.DAILY_CONTRACTS_COMMIT });
    }
    async cancelAllDailyContracts() {
        return this.request({ method: 'POST', url: API_ROUTES.DAILY_CONTRACTS_CANCEL });
    }
    async cancelOneDailyContracts(date) {
        return this.request({ method: 'DELETE', url: `${API_ROUTES.DAILY_CONTRACTS_CANCEL}/${date}` });
    }
}
export const apiClient = new ApiClient();
//# sourceMappingURL=api-client.js.map